import React from 'react';

import {
  Box,
  Button,
  Chip,
  Divider,
  Modal,
  Typography
} from '@mui/material';

import PartyHealthChip from '../../../../../components/utils/PartyHealthChip';
import { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';
import Copyable from '../../../../../components/utils/Copyable';
import NA from '../../../../../components/utils/NA';

import style from "../../../../../global.scss";
import "../../../../../components/modals/styles.scss";

const Field = ({ label, value, copyable }) => (
  <Box minWidth={"150px"} flex={"1 1 150px"} mb={"10px"}>
    <Typography variant='caption' color={"grey"} display={"block"}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ wordBreak: "break-all" }}>
      {
        (value === null || value === undefined || value === "")
        ? <NA />
        : copyable ? <Copyable>{String(value)}</Copyable> : String(value)
      }
    </Typography>
  </Box>
);

const Section = ({ title, accent, count, children }) => (
  <Box mb={"18px"}>
    <Box className='flexCenter_Row' gap={"10px"} mb={"8px"}>
      <Typography variant='subtitle2' sx={{ color: accent }}>
        {title}
      </Typography>
      <Chip size='small' label={count} sx={{ background: `${accent}20`, color: accent }} />
    </Box>
    {children}
  </Box>
);

/**
 * Everything on file for one vendor — the row itself plus every domestic bank
 * account and every international beneficiary attached to it. This is the view
 * that answers "why does repair think this vendor is broken".
 */
const VendorDetailsModal = ({ open, setOpen, vendor }) => {
  const handleClose = () => setOpen(false);

  if (!vendor) return null;

  const bankAccounts = vendor.associated_vendor_bank_account || [];
  const beneficiaries = vendor.associated_international_vendor || [];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="vendor-details-title">
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box className="modal" maxWidth={"900px"} width={"90vw"}>
          <Box sx={{ margin: "15px 15px 10px 15px" }}>
            <Box className='flexCenterSBRow'>
              <Typography variant='h6' sx={{ color: style["primary"] }}>
                {vendor.name || "Vendor"}
              </Typography>
              <PartyHealthChip health={vendor.health} />
            </Box>
            <Typography variant='caption' color={"grey"}>
              {vendor.health?.detail}
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box sx={{ margin: "15px", maxHeight: "65vh", overflowY: "auto" }}>

            <Section title="Vendor" accent={TILE_ACCENT.neutral} count={1}>
              <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                <Field label="Vendor id" value={vendor.vendor_id} copyable />
                <Field label="Legal name" value={vendor.legal_name} />
                <Field label="Contact" value={vendor.contact_name} />
                <Field label="Email" value={vendor.email} />
                <Field label="Phone" value={[vendor.phone_country_code, vendor.phone].filter(Boolean).join(" ")} />
                <Field label="Entity type" value={vendor.entity_type} />
                <Field label="Tax id" value={vendor.tax_id ? `${vendor.tax_id} (${vendor.tax_id_type || "—"})` : null} />
                <Field label="Status" value={vendor.status} />
                <Field label="Active" value={vendor.is_active ? "Yes" : "No"} />
                <Field label="Domestic" value={vendor.is_domestic ? "Yes" : "No"} />
                <Field label="International" value={vendor.is_international ? "Yes" : "No"} />
                <Field label="External" value={vendor.is_external ? "Yes" : "No"} />
                <Field label="Party id" value={vendor.ref_party_id} copyable />
                <Field label="Company" value={vendor.ref_company_id?.company_name} />
                <Field label="Company id" value={vendor.ref_company_id?.company_id} copyable />
                <Field label="Address" value={[vendor.addr_address1, vendor.addr_address2, vendor.addr_city, vendor.addr_state, vendor.addr_country, vendor.addr_zip5].filter(Boolean).join(", ")} />
              </Box>
            </Section>

            <Section
              title="Domestic bank accounts · pay_vendor_account"
              accent={bankAccounts.length ? TILE_ACCENT.good : TILE_ACCENT.warn}
              count={bankAccounts.length}>
              {
                bankAccounts.length === 0
                ?
                <Typography variant='body2' color={"grey"}>
                  No domestic bank account on file.
                </Typography>
                :
                bankAccounts.map((account, index) => (
                  <Box
                    key={index}
                    mb={"10px"}
                    sx={{
                      border: "1px solid rgba(44, 45, 47, 1)",
                      borderRadius: "10px",
                      padding: "12px",
                    }}>
                    <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                      <Field label="Account id" value={account.id} copyable />
                      <Field label="Holder" value={account.account_holder_name} />
                      <Field label="Account number" value={account.account_number} copyable />
                      <Field label="ACH routing" value={account.ach_routing} copyable />
                      <Field label="Wire routing" value={account.wire_routing} copyable />
                      <Field label="Bank" value={account.ref_bank_id?.bank} />
                      <Field label="Account type" value={account.ref_account_type_id?.type} />
                      <Field label="Default" value={account.is_default ? "Yes" : "No"} />
                      <Field label="Verification" value={account.wy_verification_status} />
                      <Field label="Verification error" value={account.wy_verification_error} />
                      <Field label="Rails" value={
                        [
                          account.wy_ach_enabled && "ACH",
                          account.wy_rtp_enabled && "RTP",
                          account.wy_rfp_enabled && "RFP",
                          account.wy_zelle_enabled && "Zelle",
                        ].filter(Boolean).join(", ")
                      } />
                      <Field label="Created" value={account.created_at} />
                    </Box>
                  </Box>
                ))
              }
            </Section>

            <Section
              title="International beneficiaries · pay_vendor_beneficiaries"
              accent={beneficiaries.length ? TILE_ACCENT.good : TILE_ACCENT.warn}
              count={beneficiaries.length}>
              {
                beneficiaries.length === 0
                ?
                <Typography variant='body2' color={"grey"}>
                  No international beneficiary on file.
                </Typography>
                :
                beneficiaries.map((beneficiary, index) => (
                  <Box
                    key={index}
                    mb={"10px"}
                    sx={{
                      border: "1px solid rgba(44, 45, 47, 1)",
                      borderRadius: "10px",
                      padding: "12px",
                    }}>
                    <Box display={"flex"} flexWrap={"wrap"} gap={"12px"}>
                      <Field label="Row id" value={beneficiary.id} copyable />
                      <Field label="Beneficiary id" value={beneficiary.beneficiary_id} copyable />
                      <Field label="Beneficiary country" value={beneficiary.beneficiary_country} />
                      <Field label="Bank country" value={beneficiary.bank_country} />
                      <Field label="Currency" value={beneficiary.currency} />
                      <Field label="Account number" value={beneficiary.account_number} copyable />
                      <Field label="IBAN" value={beneficiary.iban} copyable />
                      <Field label="BIC / SWIFT" value={beneficiary.bic_swift} copyable />
                      <Field label="Routing code" value={
                        beneficiary.routing_code_value
                        ? `${beneficiary.routing_code_value} (${beneficiary.routing_code_type || "—"})`
                        : null
                      } />
                      <Field label="Payment types" value={beneficiary.payment_types} />
                      <Field label="Created" value={beneficiary.created_at} />
                    </Box>
                  </Box>
                ))
              }
            </Section>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='outlined' onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default VendorDetailsModal;
